import { useState, useRef, useEffect, useMemo } from 'react';
import { Plus, Edit2, Trash2 } from 'lucide-react';
import { Button, Card, Badge } from '@/components/ui';
import { useSettingsStore, useItemStore } from '@/stores';
import { useToast } from '@/components/ui/Toast';
import { useConfirm } from '@/components/ui/ConfirmDialog';
import { CategoryForm } from './CategoryForm';
import { SubCategoryList } from './SubCategoryList';
import type { Category } from '@perfin/shared';

export function CategorySection() {
  const { categories, removeCategory } = useSettingsStore();
  const items = useItemStore((state) => state.items);
  const toast = useToast();
  const { confirmDelete } = useConfirm();

  // Count items with itemGroups for each category (for deletion warning)
  const itemGroupCountsByCategory = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const item of items) {
      if (item.category && item.itemGroup && item.itemGroup !== 'None') {
        counts[item.category] = (counts[item.category] || 0) + 1;
      }
    }
    return counts;
  }, [items]);

  const [showForm, setShowForm] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [expandedCategoryId, setExpandedCategoryId] = useState<string | null>(null);
  const expandedRef = useRef<HTMLDivElement>(null);

  // Scroll expanded panel into view
  useEffect(() => {
    if (expandedCategoryId && expandedRef.current) {
      expandedRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [expandedCategoryId]);

  const handleEdit = (category: Category) => {
    setEditingCategory(category);
    setShowForm(true);
  };

  const handleDelete = (category: Category) => {
    if (category.isDefault) {
      toast.error('Cannot delete default categories');
      return;
    }

    // Build warning message including itemGroup count if applicable
    const itemGroupCount = itemGroupCountsByCategory[category.id] || 0;
    let message = `Are you sure you want to delete "${category.name}"? This cannot be undone.`;
    if (itemGroupCount > 0) {
      message += `\n\nWarning: ${itemGroupCount} item${itemGroupCount === 1 ? '' : 's'} have Item Groups under this category. Those items will retain their Item Group values but the category association will be lost.`;
    }

    confirmDelete({
      title: 'Delete Category',
      message,
      onConfirm: async () => {
        try {
          await removeCategory(category.id);
          toast.success('Category deleted');
        } catch (error) {
          console.error('Delete failed:', error);
          toast.error('Failed to delete category');
        }
      },
    });
  };

  const handleFormClose = () => {
    setShowForm(false);
    setEditingCategory(null);
  };

  const getSortedSubCategories = (category: Category) => {
    return [...category.subCategories].sort((a, b) => a.name.localeCompare(b.name));
  };

  return (
    <div className="space-y-6 pb-24">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 md:gap-0">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Categories</h2>
          <p className="text-sm text-gray-500 mt-1">
            Manage categories and sub-categories for organizing your items
          </p>
        </div>
        <Button onClick={() => setShowForm(true)} leftIcon={<Plus className="h-4 w-4" />}>
          Add Category
        </Button>
      </div>

      <Card padding="none">
        <div className="divide-y divide-gray-200">
          {categories.length === 0 ? (
            <div className="p-6 text-center text-gray-500">
              No categories yet. Add your first category to get started.
            </div>
          ) : (
            categories.map((category) => {
              const sortedSubCategories = getSortedSubCategories(category);
              const isExpanded = expandedCategoryId === category.id;

              return (
                <div key={category.id} className="bg-white">
                  {/* Main row with category and subcategories */}
                  <div className="flex items-stretch">
                    {/* Category name - 40% mobile, 25% desktop */}
                    <div className="w-2/5 md:w-1/4 p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-2 bg-gray-50 border-r border-gray-200">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="inline-flex items-center px-3 py-1.5 rounded-md text-sm font-medium bg-gray-700 text-white">
                          {category.name}
                        </span>
                        {category.isDefault && (
                          <Badge variant="secondary" size="sm">
                            Default
                          </Badge>
                        )}
                      </div>
                      {/* Category actions */}
                      <div className="flex flex-col md:flex-row items-end md:items-center gap-1 self-end md:self-auto">
                        <button
                          type="button"
                          onClick={() => handleEdit(category)}
                          className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded"
                          title="Edit"
                        >
                          <Edit2 className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(category)}
                          className={`p-1.5 rounded ${
                            category.isDefault
                              ? 'text-gray-300 cursor-not-allowed'
                              : 'text-gray-400 hover:text-red-600 hover:bg-red-50'
                          }`}
                          title={category.isDefault ? 'Cannot delete default category' : 'Delete'}
                          disabled={category.isDefault}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>

                    {/* Subcategories - 60% mobile, 75% desktop */}
                    <div className="w-3/5 md:w-3/4 p-4 flex items-center gap-4">
                      {/* Subcategory badges */}
                      <div className="flex-1 min-w-0 flex flex-wrap gap-2">
                        {sortedSubCategories.length === 0 ? (
                          <span className="text-sm text-gray-400 italic">No subcategories</span>
                        ) : (
                          sortedSubCategories.map((subCat) => (
                            <span
                              key={subCat.id}
                              className="inline-flex items-center px-2.5 py-1 rounded-md text-sm bg-gray-100 text-gray-700"
                            >
                              {subCat.name}
                            </span>
                          ))
                        )}
                      </div>

                      {/* Manage subcategories button */}
                      <button
                        type="button"
                        onClick={() => setExpandedCategoryId(isExpanded ? null : category.id)}
                        className="px-2 py-1 text-xs text-primary-600 hover:bg-primary-50 rounded flex-shrink-0"
                      >
                        {isExpanded ? 'Close' : 'Manage'}
                      </button>
                    </div>
                  </div>

                  {/* Expanded subcategory management */}
                  {isExpanded && (
                    <div ref={expandedRef} className="border-t border-gray-200 bg-gray-50 p-4">
                      <SubCategoryList category={category} />
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </Card>

      {/* Add/Edit Category Modal */}
      {showForm && (
        <CategoryForm
          category={editingCategory}
          onClose={handleFormClose}
        />
      )}
    </div>
  );
}
